import {
  EDITABLE_ITC_STATUSES,
  LABELS,
  otherExpenseCreateSchema,
  otherExpenseUpdateSchema,
  yesterdayInTimezone,
} from '@mm/shared';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Building2, Paperclip, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { api, ApiError, type QueryParams } from '../../api/client';
import { AppIcon } from '../../components/AppIcon';
import { AttachmentsPanel } from '../../components/AttachmentsPanel';
import { ExportButton } from '../../components/ExportButton';
import { DateRangeFilter, useDateFilter } from '../../components/DateRangeFilter';
import { Button } from '../../components/ui/Button';
import { Card, PageHeader } from '../../components/ui/display';
import { Input } from '../../components/ui/inputs';
import { ConfirmDialog, Modal } from '../../components/ui/Modal';
import { SearchableSelect } from '../../components/ui/SearchableSelect';
import { useToast } from '../../components/ui/Toast';
import { type FieldDef, type FormValues, ResourceForm } from '../../forms/ResourceForm';
import { useDebounce } from '../../hooks/useDebounce';
import { useLookups } from '../../hooks/useLookups';
import { DataTable } from '../../tables/DataTable';
import type { OtherExpense, OtherSummary } from '../../types/models';
import { formatDate, formatMoney, formatPercent } from '../../utils/format';
import { useAuth } from '../auth/AuthProvider';
import { ItcBadge, Stat } from '../marketing/MarketingPage';
import { enumOptions, gstRateOptions, trimRate } from '../masters/configs';
import { sources } from '../masters/sources';

const COMPANY = '__company';

const detailFields: FieldDef[] = [
  {
    name: 'appId',
    label: 'App',
    type: 'select',
    emptyLabel: 'Company-level (no app)',
    remote: sources.app(),
    help: 'Leave empty for shared costs such as salary or office rent',
  },
  {
    name: 'amountBeforeGst',
    label: 'Amount before GST',
    type: 'decimal',
    required: true,
  },
  {
    name: 'gstRate',
    label: 'GST rate',
    type: 'select',
    required: true,
    options: (l, c) => gstRateOptions(l, c, 'gstRate'),
    help: 'Use 0% for salary and other costs without GST',
  },
  { name: 'vendorName', label: 'Vendor', type: 'text', placeholder: 'Amazon Web Services' },
  { name: 'vendorGstin', label: 'Vendor GSTIN', type: 'text' },
  { name: 'invoiceNumber', label: 'Invoice number', type: 'text' },
  { name: 'invoiceDate', label: 'Invoice date', type: 'date' },
  {
    name: 'itcStatus',
    label: 'ITC status',
    type: 'select',
    options: enumOptions(EDITABLE_ITC_STATUSES, LABELS.itcStatus),
    help: 'GST on a new expense is eligible input credit unless you choose otherwise',
  },
  { name: 'notes', label: 'Notes', type: 'textarea', wide: true },
];

const CATEGORY_HELP = 'Add, rename or remove categories in Settings → Expense categories';

const createFields: FieldDef[] = [
  { name: 'transactionDate', label: 'Date', type: 'date', required: true },
  {
    name: 'categoryId',
    label: 'Category',
    type: 'select',
    required: true,
    remote: sources.expenseCategory(),
    help: CATEGORY_HELP,
  },
  {
    name: 'currencyCode',
    label: 'Currency',
    type: 'select',
    required: true,
    remote: sources.currency(),
    help: 'Non-INR amounts use the month’s rate from Currency Rates',
  },
  ...detailFields,
];

const editFields: FieldDef[] = [
  { name: 'transactionDate', label: 'Date', type: 'date', required: true },
  {
    name: 'categoryId',
    label: 'Category',
    type: 'select',
    required: true,
    remote: sources.expenseCategory(),
    help: CATEGORY_HELP,
  },
  ...detailFields,
];

const str = (v: unknown) => (v == null ? '' : String(v));

type Editing = { mode: 'create' } | { mode: 'edit'; row: OtherExpense } | null;

/** Other (non-marketing) expenses: salary, servers, software, office… */
export function ExpensesPage() {
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: lookups } = useLookups();
  const [range, setRange] = useDateFilter('thisMonth');
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState('-transactionDate');
  const [editing, setEditing] = useState<Editing>(null);
  const [deleting, setDeleting] = useState<OtherExpense | null>(null);
  const [pendingDuplicate, setPendingDuplicate] = useState<{
    values: FormValues;
    message: string;
  } | null>(null);
  const debounced = useDebounce(search);
  const canWrite = can('transactions.write');

  const { appId, ...rest } = filters;
  const filterParams: QueryParams = {
    startDate: range.startDate,
    endDate: range.endDate,
    ...rest,
    ...(appId === COMPANY ? { companyOnly: 'true' } : appId ? { appId } : {}),
  };
  const list = useQuery({
    queryKey: ['other-expenses', 'list', filterParams, page, sort, debounced],
    queryFn: () =>
      api.list<OtherExpense>('/other-expenses', {
        ...filterParams,
        page,
        pageSize: 50,
        sort,
        search: debounced,
      }),
    placeholderData: keepPreviousData,
  });
  const summary = useQuery({
    queryKey: ['other-expenses', 'summary', filterParams],
    queryFn: () => api.get<OtherSummary>('/other-expenses/summary', filterParams),
    placeholderData: keepPreviousData,
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['other-expenses'] });
    void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    void queryClient.invalidateQueries({ queryKey: ['morning-entry'] });
    void queryClient.invalidateQueries({ queryKey: ['pnl'] });
  };

  const remove = useMutation({
    mutationFn: (row: OtherExpense) => api.delete(`/other-expenses/${row.id}`),
    onSuccess: () => {
      toast.success('Expense deleted');
      setDeleting(null);
      invalidate();
    },
    onError: (err) => {
      toast.error(err instanceof ApiError ? err.message : 'Delete failed');
      setDeleting(null);
    },
  });

  const createExpense = async (values: FormValues, confirmDuplicate = false) => {
    await api.post('/other-expenses', { ...values, confirmDuplicate });
    toast.success('Expense added');
    setEditing(null);
    setPendingDuplicate(null);
    invalidate();
  };

  const setFilter = (name: string, value: string) => {
    setFilters((f) => ({ ...f, [name]: value }));
    setPage(1);
  };

  const s = summary.data;
  const editRow = editing?.mode === 'edit' ? editing.row : null;
  const defaultRate = trimRate(lookups?.gstRates.find((r) => r.isDefault)?.rate ?? '18');

  return (
    <>
      <PageHeader
        title="Other expenses"
        description="Salary, servers, software and other costs. The amount before GST is the expense; GST is tracked separately. Expenses without an app are company-level."
        actions={
          <>
            <ExportButton report="other-expenses" params={{ ...filterParams, search: debounced }} />
            <DateRangeFilter
              value={range}
              onChange={(v) => {
                setRange(v);
                setPage(1);
              }}
            />
            {canWrite && (
              <Button
                icon={<Plus className="size-4" />}
                onClick={() => setEditing({ mode: 'create' })}
              >
                Add expense
              </Button>
            )}
          </>
        }
      />

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Stat label="Expenses (before GST)" value={formatMoney(s?.amountBeforeGst)} />
        <Stat label="GST paid" value={formatMoney(s?.gstAmount)} tone="gst" />
        <Stat label="Total (cash outflow)" value={formatMoney(s?.totalAmount)} />
        <Stat label="Entries" value={s ? String(s.count) : '—'} />
      </div>
      {s && s.byCategory.length > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          {s.byCategory.map((c) => (
            <button
              key={c.category.id}
              type="button"
              onClick={() =>
                setFilter('categoryId', filters.categoryId === c.category.id ? '' : c.category.id)
              }
              className={
                'rounded-full border px-3 py-1 text-xs ' +
                (filters.categoryId === c.category.id
                  ? 'border-brand-500 bg-brand-50 text-brand-700'
                  : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300')
              }
            >
              {c.category.name}{' '}
              <span className="num font-medium">{formatMoney(c.amountBeforeGst)}</span>
            </button>
          ))}
        </div>
      )}

      <Card bodyClassName="p-0">
        <div className="grid grid-cols-1 gap-3 border-b border-slate-100 p-4 sm:grid-cols-2 xl:grid-cols-4">
          <div className="relative">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search vendor, invoice, notes…"
              className="pl-9"
              aria-label="Search"
            />
          </div>
          <SearchableSelect
            ariaLabel="Category"
            placeholder="All categories"
            clearable
            value={filters.categoryId ?? ''}
            remote={sources.expenseCategory({})}
            onChange={(v) => setFilter('categoryId', v)}
          />
          <SearchableSelect
            ariaLabel="App"
            placeholder="All apps and company-level"
            clearable
            value={filters.appId === COMPANY ? '' : (filters.appId ?? '')}
            remote={sources.app({})}
            onChange={(v) => setFilter('appId', v)}
          />
          <Button
            variant={filters.appId === COMPANY ? 'primary' : 'secondary'}
            icon={<Building2 className="size-4" />}
            onClick={() => setFilter('appId', filters.appId === COMPANY ? '' : COMPANY)}
          >
            Company-level only
          </Button>
        </div>
        <DataTable<OtherExpense>
          tableId="expenses"
          rows={list.data?.data}
          rowKey={(r) => r.id}
          loading={list.isLoading}
          meta={list.data?.meta}
          onPageChange={setPage}
          sort={sort}
          onSortChange={setSort}
          onRowClick={canWrite ? (row) => setEditing({ mode: 'edit', row }) : undefined}
          columns={[
            {
              key: 'transactionDate',
              header: 'Date',
              sortKey: 'transactionDate',
              render: (r) => (
                <span className="whitespace-nowrap">{formatDate(r.transactionDate)}</span>
              ),
            },
            {
              key: 'category',
              header: 'Category',
              sortKey: 'category',
              render: (r) => (
                <div>
                  <p className="font-medium text-slate-900">{r.category.name}</p>
                  {r.vendorName && <p className="text-xs text-slate-400">{r.vendorName}</p>}
                </div>
              ),
            },
            {
              key: 'app',
              header: 'App',
              sortKey: 'app',
              render: (r) =>
                r.app ? (
                  <span className="flex items-center gap-2">
                    <AppIcon src={r.app.iconUrl} name={r.app.name} size={22} />
                    {r.app.name}
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-slate-500">
                    <Building2 className="size-4" /> Company
                  </span>
                ),
            },
            {
              key: 'amountBeforeGst',
              header: 'Before GST',
              align: 'right',
              sortKey: 'amountBeforeGst',
              render: (r) => (
                <div>
                  <p>{formatMoney(r.amountBeforeGstInr)}</p>
                  {r.currencyCode !== 'INR' && (
                    <p className="text-xs text-slate-400">
                      {formatMoney(r.amountBeforeGst, r.currencyCode)}
                    </p>
                  )}
                </div>
              ),
            },
            {
              key: 'gstRate',
              header: 'Rate',
              align: 'right',
              render: (r) => formatPercent(r.gstRate),
            },
            {
              key: 'gst',
              header: 'GST',
              align: 'right',
              render: (r) => <span className="text-violet-700">{formatMoney(r.gstAmountInr)}</span>,
            },
            {
              key: 'totalAmount',
              header: 'Total',
              align: 'right',
              sortKey: 'totalAmount',
              render: (r) => (
                <span className="font-medium text-slate-900">{formatMoney(r.totalAmountInr)}</span>
              ),
            },
            {
              key: 'itcStatus',
              header: 'ITC',
              render: (r) =>
                Number(r.gstAmount) > 0 ? (
                  <ItcBadge status={r.itcStatus} />
                ) : (
                  <span className="text-slate-300">—</span>
                ),
            },
            {
              key: 'files',
              header: <Paperclip className="size-4" aria-label="Attachments" />,
              render: (r: OtherExpense) =>
                r.attachmentCount ? (
                  <span
                    className="inline-flex items-center gap-0.5 text-xs text-slate-500"
                    title={`${r.attachmentCount} attachment(s)`}
                  >
                    <Paperclip className="size-3.5" />
                    {r.attachmentCount}
                  </span>
                ) : null,
            },
            ...(canWrite
              ? [
                  {
                    key: '__actions',
                    header: <span className="sr-only">Actions</span>,
                    align: 'right' as const,
                    render: (r: OtherExpense) => (
                      <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                          aria-label="Edit expense"
                          onClick={() => setEditing({ mode: 'edit', row: r })}
                        >
                          <Pencil className="size-4" />
                        </button>
                        <button
                          type="button"
                          className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"
                          aria-label="Delete expense"
                          onClick={() => setDeleting(r)}
                        >
                          <Trash2 className="size-4" />
                        </button>
                      </div>
                    ),
                  },
                ]
              : []),
          ]}
        />
      </Card>

      <Modal
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={
          editRow
            ? `Edit expense — ${editRow.category.name}, ${formatDate(editRow.transactionDate)}`
            : 'Add expense'
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setEditing(null)}>
              Cancel
            </Button>
            <Button type="submit" form="expense-form">
              {editRow ? 'Save changes' : 'Create'}
            </Button>
          </>
        }
      >
        {editing && (
          <ResourceForm
            key={editRow?.id ?? 'new'}
            formId="expense-form"
            fields={editRow ? editFields : createFields}
            schema={editRow ? otherExpenseUpdateSchema : otherExpenseCreateSchema}
            isEdit={!!editRow}
            lookups={lookups}
            defaultValues={
              editRow
                ? {
                    transactionDate: editRow.transactionDate,
                    categoryId: editRow.categoryId,
                    appId: str(editRow.appId),
                    amountBeforeGst: String(Number(editRow.amountBeforeGst)),
                    gstRate: trimRate(editRow.gstRate),
                    vendorName: str(editRow.vendorName),
                    vendorGstin: str(editRow.vendorGstin),
                    invoiceNumber: str(editRow.invoiceNumber),
                    invoiceDate: str(editRow.invoiceDate),
                    itcStatus: (EDITABLE_ITC_STATUSES as readonly string[]).includes(
                      editRow.itcStatus,
                    )
                      ? editRow.itcStatus
                      : '',
                    notes: str(editRow.notes),
                  }
                : {
                    transactionDate: yesterdayInTimezone(),
                    categoryId: '',
                    currencyCode: 'INR',
                    appId: '',
                    amountBeforeGst: '',
                    gstRate: defaultRate,
                    vendorName: '',
                    vendorGstin: '',
                    invoiceNumber: '',
                    invoiceDate: '',
                    itcStatus: '',
                    notes: '',
                  }
            }
            onSubmit={async (values: FormValues) => {
              if (editRow) {
                await api.put(`/other-expenses/${editRow.id}`, {
                  ...values,
                  updatedAt: editRow.updatedAt,
                });
                toast.success('Expense updated');
                setEditing(null);
                invalidate();
                return;
              }
              try {
                await createExpense(values);
              } catch (err) {
                // Expenses can repeat: an identical one only needs confirmation.
                if (
                  err instanceof ApiError &&
                  err.status === 409 &&
                  /Save anyway/.test(err.message)
                ) {
                  setPendingDuplicate({ values, message: err.message });
                  return;
                }
                throw err;
              }
            }}
          />
        )}
        {editRow && (
          <AttachmentsPanel entityType="OtherExpense" entityId={editRow.id} onChange={invalidate} />
        )}
      </Modal>

      <ConfirmDialog
        open={pendingDuplicate !== null}
        title="Possible duplicate"
        tone="primary"
        message={pendingDuplicate?.message}
        confirmLabel="Save anyway"
        onConfirm={() => {
          if (pendingDuplicate) {
            createExpense(pendingDuplicate.values, true).catch((err) =>
              toast.error(err instanceof ApiError ? err.message : 'Saving failed'),
            );
          }
        }}
        onCancel={() => setPendingDuplicate(null)}
      />

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this expense?"
        message={
          deleting && (
            <>
              {deleting.category.name} on {formatDate(deleting.transactionDate)} (
              {formatMoney(deleting.amountBeforeGstInr)}). The deletion is recorded in the audit
              log.
            </>
          )
        }
        confirmLabel="Delete"
        loading={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}
